import { useLazyQuery } from "@apollo/client";
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useRecoilState } from "recoil";

import { analytics } from "MVVM/Analytics";
import { ModalType } from "../../@types";
import { GET_RIOT_LINK_STATUS } from "../../GraphQL/user";
import { AllstarModalState } from "../../State/modals";
import { isAuthPath } from "../../Utilities/Auth/loginRedirect";
import { useUser } from "../useUser";
import { needsRiotRelink, shouldShowRiotRelinkPrompt } from "./decide";

const REFRESH_THROTTLE_MS = 30_000;

const storageKey = (userId: string) => `riot-relink-prompt:${userId}`;

const readLastShownAt = (userId: string): number | null => {
  try {
    return Number(localStorage.getItem(storageKey(userId))) || null;
  } catch {
    return null;
  }
};

const writeLastShownAt = (userId: string, now: number) => {
  try {
    localStorage.setItem(storageKey(userId), String(now));
  } catch {
    // storage unavailable: promptedUsers keeps the prompt from reopening this session
  }
};

export const useRiotRelinkPrompt = () => {
  const { allstarUser, setAllstarUser } = useUser();
  const [fetchRiotStatus, { data: statusData }] = useLazyQuery(
    GET_RIOT_LINK_STATUS,
    { fetchPolicy: "network-only", onError: () => {} },
  );
  const { pathname } = useLocation();
  const [{ isOpen }, setModalState] = useRecoilState(AllstarModalState);
  const lastRefreshAt = useRef(0);
  const promptedUsers = useRef(new Set<string>());

  const loggedIn = !!allstarUser?.loggedIn;
  const userId: string | undefined = allstarUser?.user?._id;
  const riot = allstarUser?.user?.riot;
  const needsRelink = loggedIn && needsRiotRelink(riot);

  useEffect(() => {
    if (!userId) return;
    const now = Date.now();
    const show = shouldShowRiotRelinkPrompt({
      loggedIn,
      riot,
      pathname,
      modalOpen: isOpen !== ModalType.None,
      lastShownAt: promptedUsers.current.has(userId)
        ? now
        : readLastShownAt(userId),
      now,
    });
    if (show) {
      setModalState((prev) => ({ ...prev, isOpen: ModalType.RiotRelink }));
      promptedUsers.current.add(userId);
      writeLastShownAt(userId, now);
      analytics.track("Riot Relink Prompt Shown");
    } else if (isOpen === ModalType.RiotRelink && !needsRelink) {
      setModalState((prev) => ({ ...prev, isOpen: ModalType.None }));
    }
  }, [loggedIn, userId, riot, pathname, isOpen, needsRelink, setModalState]);

  useEffect(() => {
    const fresh = statusData?.me?.user?.riot;
    if (!fresh) return;
    setAllstarUser((prev: any) => ({
      ...prev,
      user: { ...prev.user, riot: { ...prev.user?.riot, ...fresh } },
    }));
  }, [statusData, setAllstarUser]);

  useEffect(() => {
    if (!needsRelink || isAuthPath(pathname)) return;
    const refresh = () => {
      const now = Date.now();
      if (now - lastRefreshAt.current < REFRESH_THROTTLE_MS) return;
      lastRefreshAt.current = now;
      fetchRiotStatus();
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") refresh();
    };
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [needsRelink, pathname, fetchRiotStatus]);

  return { needsRelink };
};
